import axios from "axios";
import { StartUrl } from "../../configs/url";

// Global token management
let authToken = null;

// Function to set auth token globally
export const setAuthToken = (token) => {
  authToken = token;
  // Also store in localStorage for persistence
  if (token) {
    localStorage.setItem("keycloak_token", token);
  } else {
    localStorage.removeItem("keycloak_token");
  }
};

// Function to get auth token
export const getAuthToken = () => {
  // First try to get from memory
  if (authToken) {
    return authToken;
  }

  // Then try to get from localStorage
  const storedToken = localStorage.getItem("keycloak_token");
  if (storedToken) {
    authToken = storedToken;
    return storedToken;
  }

  // Finally try to get from window.keycloak (fallback)
  if (window.keycloak && window.keycloak.token) {
    authToken = window.keycloak.token;
    return window.keycloak.token;
  }

  return null;
};

// Function to clear auth token
export const clearAuthToken = () => {
  authToken = null;
  localStorage.removeItem("keycloak_token");
};

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: StartUrl,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add/refresh auth token
apiClient.interceptors.request.use(
  (config) => {
    // If Keycloak is present, try to refresh if expiring soon
    if (window.keycloak) {
      return window.keycloak
        .updateToken(30)
        .then(() => {
          // Always keep our global token in sync
          if (window.keycloak.token) {
            setAuthToken(window.keycloak.token);
          }
          const token = getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        })
        .catch(() => {
          // If refresh fails, proceed without changing headers; response interceptor will handle 401
          const token = getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        });
    }

    // Fallback when Keycloak is not available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config || {};

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark to avoid infinite retry loops
      originalRequest._retry = true;

      if (window.keycloak) {
        return window.keycloak
          .updateToken(30)
          .then((refreshed) => {
            if (refreshed && window.keycloak.token) {
              setAuthToken(window.keycloak.token);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${window.keycloak.token}`;
              return apiClient.request(originalRequest);
            }
            // Not refreshed (token still valid?) – retry once with current token
            const token = getAuthToken();
            if (token) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient.request(originalRequest);
            }
            // No token – redirect to login
            clearAuthToken();
            window.keycloak.login();
            return Promise.reject(error);
          })
          .catch(() => {
            // Refresh failed – force login
            clearAuthToken();
            window.keycloak.login();
            return Promise.reject(error);
          });
      }
    } else if (error.response?.status === 403) {
      console.error("Forbidden access - insufficient permissions");
    } else if (error.response?.status >= 500) {
      console.error("Server error - please try again later");
    }

    return Promise.reject(error);
  }
);

export default apiClient;
